import {
  Card,
  Table,
  AdvancedFilterColumn,
} from "@velocity-ehs/component-library";
import { useTranslation } from "react-i18next";
import {
  SortDirection,
  defaultPagination,
} from "../../../supportFunctions/graphql/Queries";
import { ChangeEvent, Dispatch, useCallback, useEffect, useState } from "react";
import { Column } from "primereact/column";
import { TableBodies } from "../TableBodies";
import {
  useGetPolicies,
  AssignedStatus,
} from "../../../../hooks/useGetPolicies";
import { isEqual } from "lodash";
import { useParams } from "react-router";
import { useGetRolePolicies } from "../../../../hooks/useGetRolePolicies";
import { PoliciesAction } from "./RoleReducer";
import { SelectedRolePolicy } from "./RoleForm";
import { useGetApplicationBundles } from "../../../../hooks/useGetApplicationBundles";

const defaultParams = {
  first: 0,
  rows: 20,
  page: 0,
  sortField: "name",
  sortOrder: SortDirection.ASC,
  filters: {
    applicationBundle: {
      value: null,
    },
    name: {
      value: null,
    },
    isAssignedToRoleId: {
      value: null,
    },
    roleId: {
      value: null,
    },
    applicationBundleId: {
      value: null,
    },
  },
};

interface PoliciesCardProps {
  setRolePolicies: Dispatch<PoliciesAction>;
  roleState?: any;
  handlePolicySelect: (policy: SelectedRolePolicy) => void;
  readOnly?: boolean;
}

const PoliciesCard = ({
  setRolePolicies,
  roleState,
  handlePolicySelect,
  readOnly,
}: PoliciesCardProps) => {
  const { t } = useTranslation();
  const { id } = useParams() as { id: string };
  const [lazyParams, setLazyParams] = useState(defaultParams);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const { policies, pagination, isFetching } = useGetPolicies(lazyParams, id);

  const { policies: assignedPolicies, isFetched } = useGetRolePolicies(id, {
    first: 0,
    rows: 10000,
    page: 0,
  });

  const { applications } = useGetApplicationBundles();

  useEffect(() => {
    pagination.totalElements && setTotalRecords(pagination.totalElements);
  }, [pagination]);

  const handleGlobalSearch = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      setLazyParams((previous) => ({
        ...previous,
        filters: {
          ...previous.filters,
          name: {
            value: event.target.value,
          },
        },
      })),
    []
  );

  const handleClearFilters = () => {
    setLazyParams(defaultParams);
  };

  const isDefaultFilter = isEqual(defaultParams.filters, lazyParams.filters);

  const handlePolicy = (data: {
    id?: string;
    name?: string;
    value: boolean;
  }) => {
    const { value, ...policy } = data;
    if (value === true) {
      return setRolePolicies({ type: "ADD", payload: policy });
    }
    return setRolePolicies({ type: "REMOVE", payload: data.id });
  };

  const handleRowClick = (event: any) => {
    if (event.originalEvent.target.className === "p-inputswitch-slider") {
      return;
    }
    handlePolicySelect(event.data);
  };

  useEffect(() => {
    setRolePolicies({ type: "INIT", payload: assignedPolicies });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isFetched]);

  const onAssignedFilter = (filterValue): void => {
    setLazyParams((state: any) => ({
      ...state,
      ...defaultPagination,
      filters: {
        ...state.filters,
        roleId: {
          value: id,
        },
        isAssignedToRoleId: {
          value: filterValue,
        },
      },
    }));
  };

  const onApplicationBundleFilter = (filterValue): void => {
    setLazyParams((state: any) => ({
      ...state,
      ...defaultPagination,
      filters: {
        ...state.filters,
        applicationBundleId: {
          value: filterValue[0], // This is not accepting an array, so for now I am taking the first value
        },
      },
    }));
  };

  const AssignedStatusOptions = Object.values(AssignedStatus).map((value) => {
    return {
      id: value,
      label: value,
    };
  });

  // filter out duplicate application bundles
  // and sort them by label
  const ApplicationBundleOptions = applications
    .reduce((unique, app) => {
      // Check if we already have this bundle ID
      const exists = unique.some((item) => item.id === app.id);
      if (!exists) {
        unique.push({
          id: app.id,
          label: app.name,
        });
      }
      return unique;
    }, [])
    .sort((a, b) => a.label.localeCompare(b.label));
  return (
    <Card title={t("universal_available_policies")} full>
      <Table
        fullPage={false}
        data={policies}
        params={lazyParams}
        totalRecords={totalRecords}
        onPage={setLazyParams}
        onSort={setLazyParams}
        onGlobalSearch={handleGlobalSearch}
        isLoading={isFetching}
        clearFilterCallback={handleClearFilters}
        isClearFiltersButtonDisabled={isDefaultFilter}
        selectionMode="single"
        onRowSelect={handleRowClick}
      >
        <Column field="name" header={t("universal_policy_name")} sortable />
        <Column
          field="description"
          header={t("universal_policy_description")}
          body={TableBodies.Description}
          sortable
        />
        {AdvancedFilterColumn({
          field: "applicationBundle.name",
          filterField: "applicationBundleId",
          header: t("universal_capability_title"),
          body: (data: any) => <TableBodies.Scope {...data} />,
          options: ApplicationBundleOptions,
          onApply: onApplicationBundleFilter,
          filterParams: lazyParams.filters,
          isSingleSelect: true,
        })}
        {AdvancedFilterColumn({
          field: "isAssignedToRoleId",
          filterField: "isAssignedToRoleId",
          header: t("customer_applications_assigned_status"),
          options: AssignedStatusOptions,
          body: (data: any) => (
            <TableBodies.Assigned
              data={data}
              assignedPolicies={roleState.policies}
              setState={handlePolicy}
              readOnly={readOnly}
            />
          ),
          onApply: onAssignedFilter,
          filterParams: lazyParams.filters,
          style: { justifyContent: "center", flex: "0 0 130px" },
        })}
      </Table>
    </Card>
  );
};

export { PoliciesCard };
